import React from 'react';
import { motion } from 'framer-motion';
import { CheckCheck, Clock, User, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { buildNotificationUrl } from './notificationNavigation';

const NotificationPanel = ({ 
  notifications = [], 
  loading, 
  onMarkAsRead, 
  onMarkAllAsRead, 
  onClose 
}) => {
  const navigate = useNavigate();
  
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (eventType) => {
    if (eventType.startsWith('survey.')) return <Clock size={16} className="text-blue-500" />;
    if (eventType.startsWith('participant.')) return <User size={16} className="text-green-500" />;
    if (eventType.startsWith('dcq.')) return <MapPin size={16} className="text-purple-500" />;
    return <User size={16} className="text-gray-500" />;
  };

  const unreadNotifications = notifications.filter(n => !n.is_read);
  const hasUnread = unreadNotifications.length > 0;

  const handleNotificationClick = (notification) => {
    const payload = notification.payload || {};

    // Mark as read if unread
    if (!notification.is_read) {
      onMarkAsRead([notification.id]);
    }
    
    // Navigate based on notification type and available data
    const navigationUrl = buildNotificationUrl(notification);

    if (navigationUrl) {
      onClose(); // Close the notification panel
      navigate(navigationUrl);
    } else {
      // Fallback: Test notification or no navigation data available
      if (notification.title.includes('Test') || payload.participant_id === 'test-participant-id') {
        // Handle test notifications
        onClose();
        alert('🧪 Test notification clicked!\n\nThis is a test notification with fake data. In production, this would navigate to the relevant page with real entity IDs.');
      } else {
        // Real notification but missing navigation data
        console.log('No specific navigation available for notification:', notification);
        // Could show a "View Details" modal or navigate to a general notifications page
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="absolute right-0 mt-2 w-80 origin-top-right z-50"
    >
      <div className="rounded-xl overflow-hidden shadow-lg ring-1 ring-black/5 bg-white dark:bg-gray-800 max-h-96">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">
              Notifications
              {unreadNotifications.length > 0 && (
                <span className="ml-2 bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full">
                  {unreadNotifications.length} new
                </span>
              )}
            </h3>
            
            {hasUnread && (
              <button
                onClick={onMarkAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center"
              >
                <CheckCheck size={12} className="mr-1" />
                Mark all read
              </button>
            )}
          </div>
        </div>

        {/* Notification List */}
        <div className="max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-gray-400 mb-2">
                <User size={24} className="mx-auto" />
              </div>
              <p className="text-sm text-gray-500">No notifications yet</p>
              <p className="text-xs text-gray-400 mt-1">You'll see updates here when they arrive</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`p-4 hover:bg-gray-50 transition-colors duration-200 cursor-pointer ${
                    !notification.is_read ? 'bg-blue-50 border-l-4 border-l-blue-400' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.event_type_key)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm ${
                          !notification.is_read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                        } truncate`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2"></div>
                        )}
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {notification.body}
                      </p>
                      
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                        
                        {notification.actor_name && notification.actor_name !== 'System' && (
                          <span className="text-xs text-gray-500">
                            by {notification.actor_name}
                          </span>
                        )}
                      </div>

                      {/* Context info */}
                      {(notification.payload?.site_code || notification.payload?.participant_number) && (
                        <div className="flex items-center space-x-2 mt-2">
                          {notification.payload?.site_code && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                              Site {notification.payload.site_code}
                            </span>
                          )}
                          {notification.payload?.participant_number && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                              P{notification.payload.participant_number}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <button
              onClick={() => {
                onClose();
                navigate('/notifications');
              }}
              className="w-full text-center text-xs text-gray-600 hover:text-gray-800 font-medium"
            >
              View all notifications
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default NotificationPanel;
